import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatTabGroup } from '@angular/material/tabs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import isEqual from 'fast-deep-equal';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  first,
  map,
  Observable,
  switchMap,
} from 'rxjs';

import { DialogService } from '@blaze-shared/shared-feature';
import { ConfirmDialogInput } from '@blaze-shared/shared-ui';
import { SelectOption } from '@blaze-shared/shared-util';
import { ThemesFacade, ThemesService } from '@blaze-shared/theming-data-access';
import { AppTheme, AppThemeValue } from '@blaze-shared/theming-domain';

import { CreateThemeDialogComponent } from '../create-theme-dialog/create-theme-dialog.component';
import {
  CreateThemeDialogData,
  CreateThemeDialogResult,
  NEW_THEME_DEFAULT_NAME,
} from '../create-theme-dialog/create-theme-dialog.models';
import {
  ThemeEditorSaveDialogComponent,
  ThemeEditorSaveDialogData,
} from '../theme-editor-save-dialog/theme-editor-save-dialog.component';
import { nextAvailableThemeName } from '../theme-naming/theme-naming.utils';

const DISCARD_THEME_CHANGES_PROMPT: ConfirmDialogInput = {
  title: 'You have unsaved changes',
  description: 'Do you want to discard your changes?',
  confirmLabel: 'Discard',
  cancelLabel: 'Cancel',
};

@UntilDestroy()
@Component({
  selector: 'blaze-theme-editor-menu',
  templateUrl: './theme-editor-menu.component.html',
  styleUrls: ['./theme-editor-menu.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ThemeEditorMenuComponent implements OnInit {
  private readonly themesFacade = inject(ThemesFacade);
  private readonly themesService = inject(ThemesService);
  private readonly dialog = inject(MatDialog);
  private readonly dialogService = inject(DialogService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() projectId!: string;
  @Input() fonts: SelectOption<string, string>[] = [];
  @Input() isCurrentUserAccountRootOrDev = false;

  @ViewChild('tabs', { static: false }) tabs!: MatTabGroup;

  private selectedThemeCurrentValues: AppThemeValue | undefined;

  defaultThemes: Record<string, AppTheme> = {};
  themes: AppTheme[] = [];

  selectedTheme: AppTheme | undefined;
  isSelectedThemeChanged = false;
  appliedThemeId: string | undefined;

  isSavingInProgress$: Observable<boolean>;

  private get usedThemeNames(): string[] {
    return this.themes.map((theme) => theme.name);
  }

  constructor() {
    this.isSavingInProgress$ = this.themesFacade.isSavingInProgress$;
  }

  ngOnInit(): void {
    this.setSelectedTheme();
    this.setAppliedTheme();
    this.loadThemes();
  }

  onSelectionChange(theme: AppTheme): void {
    if (this.selectedTheme?.id === theme.id) {
      return;
    }

    if (!this.isSelectedThemeChanged) {
      this.themesFacade.selectTheme(theme.id, true);
      return;
    }

    this.dialogService
      .openConfirmDialog(DISCARD_THEME_CHANGES_PROMPT, { autoFocus: 'dialog' })
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.themesFacade.selectTheme(theme.id, true);
        }
      });
  }

  onThemeValueChange(theme: AppTheme): void {
    let areThemesEqual = false;
    if (this.selectedTheme) {
      const selectedTheme = this.themesService.extendThemeValues(this.selectedTheme);
      const updatedTheme = this.themesService.extendThemeValues(theme);
      areThemesEqual = isEqual(selectedTheme.value, updatedTheme.value);
    }
    this.isSelectedThemeChanged = !areThemesEqual;

    this.themesFacade.selectedThemeValueChanged(this.isSelectedThemeChanged);
    this.selectedThemeCurrentValues = theme.value;
    this.themesFacade.applyNewTheme(theme);

    this.cdr.markForCheck();
  }

  resetTheme(themeId: string): void {
    const theme = this.themes.find((theme) => theme.id === themeId);
    if (!theme) {
      return;
    }

    this.selectedTheme = {
      ...theme,
    };

    this.onThemeValueChange(this.selectedTheme);
  }

  apply(theme: AppTheme): void {
    if (this.isSelectedThemeChanged && theme.scope !== 'Blaze') {
      this.themesFacade.updateTheme(
        {
          ...this.selectedTheme,
          value: this.selectedThemeCurrentValues as AppThemeValue,
        } as AppTheme,
        this.projectId,
      );
    } else {
      this.themesFacade.applyTheme(theme.id);
      this.themesFacade.linkToProject(theme.id, this.projectId);
      if (theme.scope === 'Blaze') {
        this.resetTheme(theme.id);
      }
    }
  }

  save(apply: boolean): void {
    if (!this.selectedTheme) {
      return;
    }

    this.createNewTheme(this.selectedTheme, apply);
  }

  createThemeWithAi(): void {
    const usedNames = this.usedThemeNames;

    this.dialog
      .open<CreateThemeDialogComponent, CreateThemeDialogData, CreateThemeDialogResult>(
        CreateThemeDialogComponent,
        {
          autoFocus: 'first-tabbable',
          width: '520px',
          panelClass: ['bd', 'bd-dialog'],
          hasBackdrop: true,
          data: {
            projectId: this.projectId,
            name: nextAvailableThemeName(NEW_THEME_DEFAULT_NAME, usedNames),
            usedNames,
          },
        },
      )
      .afterClosed()
      .pipe(untilDestroyed(this))
      .subscribe((result) => {
        if (!result) {
          return;
        }

        const { themeId } = result;

        this.themesFacade.selectTheme(themeId, true);
        this.themesFacade.applyTheme(themeId);
        this.themesFacade.linkToProject(themeId, this.projectId);
        this.themesFacade.loadThemes(this.projectId);
      });
  }

  deleteTheme(id: string): void {
    this.themesFacade.deleteTheme(id);
  }

  private selectTheme(theme: AppTheme): void {
    this.selectedTheme = theme;
    this.isSelectedThemeChanged = false;

    this.cdr.markForCheck();
  }

  private setSelectedTheme(): void {
    this.themesFacade.selectedTheme$
      .pipe(filter(Boolean), untilDestroyed(this))
      .subscribe((theme) => {
        this.selectTheme(theme);
      });
  }

  private setAppliedTheme(): void {
    this.themesFacade.appliedTheme$
      .pipe(filter(Boolean), untilDestroyed(this))
      .subscribe((theme) => {
        this.appliedThemeId = theme.id;
      });
  }

  private createNewTheme(copiedTheme: AppTheme, applyNewTheme: boolean): void {
    const usedNames = this.usedThemeNames;

    this.dialog.open<ThemeEditorSaveDialogComponent, ThemeEditorSaveDialogData>(
      ThemeEditorSaveDialogComponent,
      {
        autoFocus: false,
        width: '375px',
        panelClass: ['bd', 'bd-dialog'],
        hasBackdrop: true,
        data: {
          willApplyNewTheme: applyNewTheme,
          name: nextAvailableThemeName(copiedTheme.name, usedNames),
          usedNames,
          save: (name: string, saveAsBlazeTheme: boolean) => {
            const value = this.isSelectedThemeChanged
              ? (this.selectedThemeCurrentValues ?? copiedTheme.value)
              : copiedTheme.value;

            this.themesFacade.createTheme(
              { name, value },
              this.projectId,
              copiedTheme,
              saveAsBlazeTheme,
            );

            return this.themesFacade.createFinished$.pipe(
              first(Boolean),
              switchMap(() => this.themesFacade.createError$),
              first(),
              map((error) => {
                if (error) {
                  throw new Error(error);
                }
              }),
            );
          },
        },
      },
    );
  }

  private loadThemes(): void {
    this.themesFacade.loadFinished$
      .pipe(
        filter(Boolean),
        switchMap(() =>
          combineLatest([this.themesFacade.themes$, this.themesFacade.defaultThemesEntities$]),
        ),
        distinctUntilChanged((a, b) => isEqual(a, b)),
        untilDestroyed(this),
      )
      .subscribe(([themes, defaults]) => {
        this.themes = themes;
        this.defaultThemes = defaults;

        this.cdr.markForCheck();
      });
  }
}
